import React, { createContext, useContext, useEffect, useState } from "react";
import * as SessionStorageWrapper from "../utils/SessionStorageWrapper";
import * as http from "../utils/http";
import { clientConfig } from "../config";
import { debounce } from "../utils/helpers";
import { MainTracker } from "../PixelTrackers/MainTracker";
import { saveAppGlobalBannerFlag } from "../utils/appGlobalBanner";
import { applyTestLabMultiTestArm, readTestLabMultiTestVariant } from "../utils/testLabMultiTestExperiment";
import { resetCustomData, setCustomData } from "../PixelTrackers/KameleoonTracker";
import { DRUGS_PRODUCT_TYPES, OTC_PRODUCT_TYPES } from "../utils/consts";

const syncConsultationSubtypeSuperProperty = (cart: Partial<CartView>) => {
  if (cart.consultation_type !== "video") {
    return;
  }
  if (cart.is_follow_up_consultation === undefined || cart.is_follow_up_consultation === null) {
    return;
  }
  MainTracker.superProperties({
    "Consultation subtype": cart.is_follow_up_consultation ? "Follow-up Videocons" : "First Videocons",
  });
};

export const clearCartFromSession = () => {
  window.eventsTracking.setData(`cart_id`, "", true);
  window.eventsTracking.setData(`cart_token`, "", true);
  window.eventsTracking.setData(`cart_mention`, "", true);
  window.eventsTracking.setData(`cart_mixpanel_title`, "", true);
  window.eventsTracking.setData(`cart_is_e_recipe`, "", true);
  window.eventsTracking.setData(`cart_country`, "", true);
};

// Kameleoon `shipping_method` vocabulary (HL-12604). Standard delivery carrier per market mirrors
// OrderShippingsManager (FR FedEx, CH Swiss Post, GB Royal Mail, else DHL); prescription-only has none.
const STANDARD_CARRIER_BY_COUNTRY: Record<string, string> = { fr: "fedex", ch: "swiss_post", gb: "royal_mail" };
const kameleoonShippingMethod = (cart: CartView): string | null => {
  if (!cart.item_confirmed) return null; // before the Choose Treatment submit the delivery tab lives in page state, not the cart
  if (cart.only_prescription || cart.selected_service_option === "only_prescription") return null;
  if (cart.selected_service_option === "self_pickup") return "self_pickup";
  if (cart.selected_service_option === "express") {
    if (cart.selected_express_tier === "same_day") return "urbify_same_day";
    if (cart.selected_express_tier === "next_day") return "urbify_next_day";
    return "uber";
  }
  return STANDARD_CARRIER_BY_COUNTRY[cart.locale_id?.split("-")[1]?.toLowerCase() ?? ""] ?? "dhl";
};

// CartContext type
type CartContextType = {
  getCart: () => CartView;
  getCartNumOfProducts: () => number;
  getSessionFields: () => { mention: string | null; id: string | null; token: string | null; mixpanel_title: string | null };
  saveCart: (cart: CartView) => void;
  restoreCartFromOrder: (country: string, language: string, orderHashID: string) => Promise<{ cartView: any; mention: string }>;
  clearCart: () => void;
  updateCart: (fields: Partial<CartView>) => void;
  updateCartAndDB: (fields: Partial<CartView>) => Promise<Record<string, string | number>>;
  addProduct: (product: CartProductView) => void;
  removeProduct: (productItemIdToRemove: number) => void;
  removeProductItemsFromCart:(productItems: number[]) => Promise<{status: string}>;
  addProductItemsToCart:(productItems: ProductItem[]) => Promise<{status: string}>;
  swapRxCartProducts:(productsToRemove: number[], productsToAdd: ProductItem[]) => Promise<CartProductView[]>;
  incrementProductItemQuantity: (productItem: ProductItem, isExpressSelected: boolean, source?: string) => Promise<{status: string, products: CartProductView[], expressStockCode: string}>;
  decrementProductItemQuantity: (productItem: ProductItem, isExpressSelected: boolean) => Promise<{status: string, products: CartProductView[], expressStockCode: string}>;
  setRiskGroupConfirmation: (riskGroupID: number, confirmed: boolean) => Promise<{status: string}>;
  cartCurrency: CountryCurrency
  setCartCurrency: (currency: CountryCurrency) => void
  activateSubscriptionInDB: (subscriptionPlanID: number) => Promise<{ status: string }>
  deactivateSubscriptionInDB: () => Promise<any>;
  // createCartByEventsTracker: (cartData:any) => Promise<any>
}

// CartState type
export type CartView = Cart & { products: CartProductView[], doctor?: { fullName: string, address: string }, showAppGlobalBanner?: boolean }

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Custom hook to use the context
export function useCartContext(): CartContextType {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
}


// Updating product quantity in DB cart (using debounce) - *NEEDS TO BE IN THIS SCOPE*
const debouncedBatchUpdateDBCartQuantity = debounce(
  async (currentCartProducts: CartProductView[], isExpressSelected = false) => {
    const productItemsWithQuantity = currentCartProducts.map((productItem) => ({
      productItemID: productItem.product_item_id,
      quantity: productItem.quantity, 
      catalogID: productItem.catalog_id,
      isAddon: productItem.is_addon,
      source: productItem.source ?? undefined,
    }));
    if (productItemsWithQuantity.length === 0) {
      return { data: { status: "ok" } };
    }
    // Send all the updated products at once in a single batch. The "Test Lab Multi Test AB" arm travels with
    // the request so the server applies the same kit rule the page shows.
    const response = await http.postToServer(
      "cart/products/update/quantity", // Assume this endpoint supports batch updates
      { productItemsWithQuantity, isExpressSelected, testLabMultiTestVariant: readTestLabMultiTestVariant() || undefined },
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]
    );

    return response;
    
  },
  1000  // Fixed delay to prevent spamming the server
);



// Provider
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [cartViewState, setCartViewStateRaw] = useState<CartView>({ products: [], timekitBooking: null });
  // Every cart write (server views included) keeps the "Test Lab Multi Test AB" arm the browser holds.
  const setCartViewState: React.Dispatch<React.SetStateAction<CartView>> = (value) =>
    setCartViewStateRaw((prev) => applyTestLabMultiTestArm(typeof value === "function" ? value(prev) : value));
  const [cartCurrency, setCartCurrency] = useState<CountryCurrency>({ locale: "de-DE", rate: 1, isDefault: true, countryID: "de", currencySign: "€" })

  // Saved for the index.html head script, which reads it on the next page load.
  useEffect(() => {
    saveAppGlobalBannerFlag(cartViewState.showAppGlobalBanner);
  }, [cartViewState.showAppGlobalBanner]);

  // Kameleoon targeting data (HL-12604): every page that saves or updates the cart (shipping load,
  // delivery option change, upsell/OTC accept, coupon, payment load) lands here. Cart prices are the
  // EUR base values (display multiplies by cartCurrency.rate), same as Mixpanel "Coupon Amount EUR".
  useEffect(() => {
    const cart = cartViewState;
    if (!cart.id) return;
    // Medication and OTC lines only: fee lines (e.g. the cannabis ID card) are not products.
    const products = (cart.products ?? []).filter((p) => [...DRUGS_PRODUCT_TYPES, ...OTC_PRODUCT_TYPES].includes(p.type ?? ""));
    // official_price is the medication price, as on Choose Treatment; item_price also includes the treatment and service fees.
    const cartValue = products.reduce((sum, p) => sum + Number(p.official_price ?? p.item_price ?? 0) * Number(p.quantity ?? 1), 0);
    setCustomData("cart_value_eur", Math.round(cartValue * 100) / 100);
    setCustomData("cart_items_count", products.length);
    setCustomData("has_otc", products.some((p) => OTC_PRODUCT_TYPES.includes(p.type ?? "")));
    setCustomData("purchase_type", cart.only_prescription || cart.selected_service_option === "only_prescription" ? "prescription_only" : "premium");
    // Reset when the cart has no coupon: Kameleoon would otherwise keep an earlier cart's coupon for the visit.
    if (cart.bonus_coupon_code) setCustomData("coupon_code", cart.bonus_coupon_code);
    else resetCustomData("coupon_code");
    const shippingMethod = kameleoonShippingMethod(cart);
    if (shippingMethod) {
      setCustomData("shipping_method", shippingMethod);
      // keep delivery_mode (set from the Choose Treatment tab) in step with the method chosen on Shipping
      setCustomData("delivery_mode", shippingMethod === "self_pickup" ? "pickup" : cart.selected_service_option === "express" ? "express" : "standard");
    }
  }, [cartViewState]);

  const getCart = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const cartFromParam = urlParams.get("crt");

    if (cartFromParam && SessionStorageWrapper.getItem("last_shipped_cart") !== cartFromParam) {
      window.eventsTracking.setData(`cart_id`, cartFromParam, true);
    }

    return cartViewState;
  }


  const getCartNumOfProducts = () => {
    return cartViewState?.products?.length;
  }

  const getSessionFields = () => {
    return {
      id: window.eventsTracking.getData("cart_id"),
      token: window.eventsTracking.getData("cart_token"),
      mention: window.eventsTracking.getData("cart_mention"),
      mixpanel_title: window.eventsTracking.getData("cart_mixpanel_title"),
      is_e_recipe: window.eventsTracking.getData("cart_is_e_recipe"),
    }
  }

  const saveCart = (cart: CartView) => {
    // 1. Save in eventsTracking
    window.eventsTracking.setData(`cart_id`, cart.id!, true);
    window.eventsTracking.setData(`cart_token`, cart.token!, true);
    window.eventsTracking.setData(`cart_is_e_recipe`, cart.is_e_recipe!, true);

    const country = cart.locale_id?.split('-')[1] ?? '';
    window.eventsTracking.setData(`cart_country`, country, true);

    if (cart.mention) {
      window.eventsTracking.setData('cart_mention', cart.mention!, true);
    }

    if (cart.mixpanel_title) {
      window.eventsTracking.setData('cart_mixpanel_title', cart.mixpanel_title!, true);
    }

    syncConsultationSubtypeSuperProperty(cart);

    // 2. Save the state
    setCartViewState(cart);

  };

  // Recreates a cart from an already-placed order (the cart is cleared on shipping
  // submit) and persists it in the session. Used both by the payment "back" handler
  // and by the shipping page when it lands without a cart (iPhone Chrome back).
  const restoreCartFromOrder = async (country: string, language: string, orderHashID: string) => {
    const response = await http.postToServer(
      `payment_v2/${country}/${language}/${orderHashID}/restore-cart`,
      {},
      [http.MIDDLEWARES.CUSTOMER_ID]
    );

    if (response.status !== 200) {
      throw new Error('Failed to restore cart');
    }

    const responseData = response.data;
    const cartView = responseData.cartView;
    const mention = responseData.mention;
    const restoredByCartID = responseData.restoredByCartID;

    window.eventsTracking.setData('cart_mention', mention, true);
    window.eventsTracking.setData('restored_by_cart_id', restoredByCartID, true);
    saveCart({
      ...cartView.cart,
      mention,
      products: cartView.products,
      timekitBooking: cartView.timekitBooking,
    });
    return { cartView, mention };
  };

  const clearCart = () => {
    clearCartFromSession();
    setCartViewState({ products: [], timekitBooking: null });
  };

  const updateCart = (fields: Partial<CartView>) => {
    setCartViewState((current) => {
      const next = { ...current, ...fields };
      if (
        fields.is_follow_up_consultation !== undefined ||
        fields.consultation_type !== undefined ||
        fields.consultation_fee !== undefined
      ) {
        syncConsultationSubtypeSuperProperty(next);
      }
      return next;
    });
  };

  const addProduct = (product: CartProductView) => {
    product.is_addon = true;
    setCartViewState((prev) => ({
      ...prev,
      products: [...prev.products, product],
    }));
  };

  

  const addProductItemsToCart = async (productItems: ProductItem[]): Promise<{ status: string }> => {
    let updatedProducts: CartProductView[] = [];
  
    // Optimistic state update
    setCartViewState((prev) => {
      updatedProducts = [...prev.products];
  
      for (const productItem of productItems) {
        const existingIndex = updatedProducts.findIndex(p => p.product_item_id == productItem.id);
  
        //the product doesnt exist in the cart
        if (existingIndex === -1) {
          const newProduct: CartProductView = {
            ...productItem,
            cart_id: cartViewState.id ?? 0,
            product_item_id: productItem.id,
            treatment_fee: 0,
            item_price: productItem.price,
            official_price: productItem.official_price,
            is_prescription_drug: false,
            is_test_kit: false,
            type: productItem.type,
            only_prescription: false,
            quantity: Number(productItem.quantity),
            quantityStr: productItem.custom_quantity_title || "1",
            imageSrc: clientConfig.IMAGES_URL + productItem.imgSrc || "",
            subscriptionPlans: [],
          }as CartProductView; 
          updatedProducts.push(newProduct);
        //the product exist so we only change quentity
        } else {
          const updatedProduct = {
            ...updatedProducts[existingIndex],
            quantity: Number(productItem.quantity),
          };
          updatedProducts[existingIndex] = updatedProduct;
        }
      }
  
      // Don't update optimistic state for any cannabis_id items
      if (productItems.some(p => p.type === "cannabis_id")) {
        return { ...prev };
      }
  
      return { ...prev, products: updatedProducts };
    });
    // Batch update DB; a refused update answers 4xx with the server cart, which replaces the optimistic state.
    let response: any;
    try {
      response = await debouncedBatchUpdateDBCartQuantity(updatedProducts);
    } catch (error: any) {
      response = error?.response ?? { data: { status: "failed" } };
    }

    if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      return { status: "ok" };
    } else {
      if (response.data.cartView) {
        const cartView = response.data.cartView;
        setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      }
      window.clientLogger.warn("Failed to update cart quantities", response);
      return { status: "failed" };
    }
  };
  
  const removeProductItemsFromCart = async (productItemIds: number[]): Promise<{ status: string }> => {
    let updatedProducts: CartProductView[] = [];
    // Optimistic state update

    updatedProducts = cartViewState.products.filter(
      (product) => !productItemIds.includes(product.product_item_id)
    );
      
    setCartViewState((prev) => {

      return { ...prev, products: updatedProducts };
    });

    // Batch update DB
    const response = await debouncedBatchUpdateDBCartQuantity(updatedProducts);

    if(response.data.status === "ok" && (!response.data.cartView || response.data.cartView?.products?.length === 0)){
      clearCart();
      return { status: "ok" };
    }else if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      return { status: "ok" };
    } else {
      window.clientLogger.warn("Failed to update cart after removal", response);
      return { status: "failed" };
    }
  };
  
  const swapRxCartProducts = async (productsToRemove: number[], productsToAdd: ProductItem[]): Promise<CartProductView[]> => {
    const originalProducts = cartViewState.products;
    
    const remainingProducts: CartProductView[] = originalProducts.filter(
      (product) => !productsToRemove.includes(product.product_item_id)
    );

    // Create CartProductView items for the new products
    const newProducts: CartProductView[] = productsToAdd.map(productItem => ({
        ...productItem,
        cart_id: cartViewState.id ?? 0,
        product_item_id: productItem.id,
        treatment_fee: 0,
        item_price: productItem.price,
        is_prescription_drug: true,
        is_test_kit: false,
        type: productItem.type,
        only_prescription: false,
        quantity: Number(productItem.quantity),
        quantityStr: productItem.custom_quantity_title || "1",
        imageSrc: productItem.imgSrc || "",
        subscriptionPlans: [],
    } as CartProductView));

    const finalProducts = [...remainingProducts, ...newProducts];

    return finalProducts;
  }
  
  // A response that was already in flight when the user clicked again must not
  // roll the UI quantity back: keep the optimistic quantity (max after "+", min after "-").
  const applyCartViewKeepingUiQuantity = (cartView: any, pick: (serverQty: number, uiQty: number) => number) => {
    setCartViewState((prev) => ({
      ...cartView.cart,
      products: cartView.products
        .map((p: CartProductView) => ({ ...p, quantity: pick(p.quantity, prev.products.find((cp) => cp.product_item_id === p.product_item_id)?.quantity ?? 0) }))
        .filter((p: CartProductView) => p.quantity > 0),
      timekitBooking: cartView.timekitBooking || null,
    }));
  };

  //This will increment the quantity of the product or add a new cart-product
  const incrementProductItemQuantity = async (productItem: ProductItem, isExpressSelected = false, source?: string): Promise<{status: string, products: CartProductView[], expressStockCode: string}> => {
    
    // Calculate updated products first
    let updatedProducts: CartProductView[] = cartViewState.products.map((p) => {
      if (p.product_item_id === productItem.id) {
        return { ...p, quantity: p.quantity + 1, ...(source && !p.source ? { source } : {}) };
      }
      return p;
    });

  
    // If the product is not found, add it to the cart
    if (!updatedProducts.some((p) => p.product_item_id === productItem.id)) {

      updatedProducts = [
        ...updatedProducts,
        {
          ...productItem,
          cart_id: cartViewState.id ?? 0,
          product_item_id: productItem.id,
          treatment_fee: 0,
          official_price: productItem.official_price,
          item_price: productItem.price,
          is_prescription_drug: false,
          is_test_kit: false,
          type: productItem.type,
          only_prescription: false,
          quantity: 1, // Set the initial quantity to 1 since it's a new product being added
          quantityStr: productItem.custom_quantity_title || "1",
          imageSrc: clientConfig.IMAGES_URL + productItem.imgSrc || "",
          subscriptionPlans: [],
          is_addon: true,
          source: source ?? null,
        } as CartProductView,
      ];
    }
    
    
    // Optimistic state update
    if (productItem.type !== "cannabis_id") { //Dont update the optimistic state for cannabis id, because of the pulsing effect
      setCartViewState((prev) => ({ ...prev, products: updatedProducts }));
    }
        
    // Update the DB in batch. A refused update (e.g. TEST_KIT_LIMIT) answers 4xx with the server cart,
    // which then replaces the optimistic quantity below.
    let response: any;
    try {
      response = await debouncedBatchUpdateDBCartQuantity(updatedProducts, isExpressSelected);
    } catch (error: any) {
      response = error?.response ?? { data: { status: "failed" } };
    }

    if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      applyCartViewKeepingUiQuantity(cartView, Math.max);
      return { status:"ok", products: cartView.products, expressStockCode: cartView.expressStockCode };

    } else {
      if (response.data.cartView) {
        const cartView = response.data.cartView;
        setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      }
      window.clientLogger.warn("Failed to update cart quantities", response);
      return { status:"failed", products: [], expressStockCode: "" };
    }
  };
  
  

  //This will decrement the quantity of the product or remove an existing cart-product
  const decrementProductItemQuantity = async (productItem: ProductItem, isExpressSelected = false): Promise<{status: string, products: CartProductView[], expressStockCode: string}> => {
    const existingProductIndex = cartViewState.products.findIndex((cp) => cp.product_item_id === productItem.id);
    if (existingProductIndex === -1) {
      return { status:"ignored", products: [], expressStockCode: "" };
    }
  
    let updatedQuantity = 0;
    
    // Calculate updated products first
    let updatedProducts: CartProductView[] = cartViewState.products.map((p, index) => {
      if (index === existingProductIndex) {
        updatedQuantity = Math.max(0, p.quantity - 1);
        return { ...p, quantity: updatedQuantity };
      }
      return p;
    }).filter((p) => p.quantity > 0);
  
    // Optimistic state update
    if (productItem.type !== "cannabis_id") { //Dont update the optimistic state for cannabis id, because of the pulsing effect
      setCartViewState((prev) => ({ ...prev, products: updatedProducts }));
    }
  
    // Update the DB (using debounced batch update); a refused update answers 4xx with the server cart.
    let response: any;
    try {
      response = await debouncedBatchUpdateDBCartQuantity(updatedProducts, isExpressSelected);
    } catch (error: any) {
      response = error?.response ?? { data: { status: "failed" } };
    }
    if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      applyCartViewKeepingUiQuantity(cartView, Math.min);
      return { status:"ok", products: cartView.products, expressStockCode: cartView.expressStockCode };

    } else {
      if (response.data.cartView) {
        const cartView = response.data.cartView;
        setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      }
      window.clientLogger.warn("Failed to update cart quantities", response);
      return { status:"failed", products: [], expressStockCode: "" };
    }
  };

  

  const setRiskGroupConfirmation = async (riskGroupID: number, confirmed: boolean): Promise<{ status: string }> => {
    const response = await http.postToServer(
      'cart/risk-group/confirm',
      { riskGroupID, confirmed },
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]
    );
    if (response.data.status === 'ok' && response.data.cartView) {
      const cartView = response.data.cartView;
      setCartViewState({ ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking || null });
      return { status: 'ok' };
    }
    return { status: 'failed' };
  };

  const removeProduct = (productItemIdToRemove: number) => {
    setCartViewState((prev) => ({
      ...prev,
      products: prev.products.filter((product) => product.product_item_id !== productItemIdToRemove),
    }));
  };


  // Maybe we won't use that, we will see...
  const updateCartAndDB = async (fields: Partial<CartView>, callBack?: (cartView:any) => void) => {
    const cartID = window.eventsTracking.getData(`cart_id`)

    if (!cartID) {
      return { status: "failed" }
    }

    try {
      const response = await http.postToServer("cart/update", { ...fields }, [http.MIDDLEWARES.CART_ORDER]);

      if (response.data.status === "ok" && response.data.cartView) {
        const cartView = response.data.cartView;
        const newCartViewState = {
          ...cartView.cart,
          products: cartView.products
        }

        setCartViewState(newCartViewState);

        if (callBack){
          callBack(newCartViewState);
        }
        
        return {status:"ok"};
        
      } else {
        window.clientLogger.error("Couldn't update cart", response);
        return { status: "failed" }
      }

    } catch (error) {
      window.clientLogger.error("Server failed to update the cart:", error);
      return { status: "failed" }
    }
  };


  const activateSubscriptionInDB = async (subscriptionPlanID: number) => {
    const response = await http.postToServer("cart/activate-subscrption", { subscriptionPlanID }, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID]);
    if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      setCartViewState({
        ...cartView.cart,
        products: cartView.products,
        timekitBooking: cartView.timekitBooking || null
      });

    } else {
      window.clientLogger.warn("Failed to deactivate subscription in DB (cart)", response);
    }

    return response
  }


  const deactivateSubscriptionInDB = async (): Promise<any> => {
    const response = await http.postToServer(
      "cart/deactivate-subscrption",
      {},
      [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.CUSTOMER_ID],
    );
    if (response.data.status === "ok" && response.data.cartView) {
      const cartView = response.data.cartView;
      setCartViewState({
        ...cartView.cart,
        products: cartView.products,
        timekitBooking: cartView.timekitBooking || null
      });

    } else {
      window.clientLogger.warn("Failed to deactivate subscription in DB (cart)", response);
    }

    return response
  }


  return (
    <CartContext.Provider value={{
      getCart,
      getSessionFields,
      saveCart,
      restoreCartFromOrder,
      clearCart,
      updateCart,
      updateCartAndDB,
      addProduct,
      removeProduct,
      removeProductItemsFromCart,
      addProductItemsToCart,
      swapRxCartProducts,
      incrementProductItemQuantity,
      decrementProductItemQuantity,
      setRiskGroupConfirmation,
      cartCurrency,
      setCartCurrency,
      activateSubscriptionInDB,
      deactivateSubscriptionInDB,
      getCartNumOfProducts,
      // createCartByEventsTracker
    }} 
    >
      {children}
    </CartContext.Provider>
  );
};
